import React, { useRef, useState } from 'react';

import '../Config.scss';
import { Button, ModalHeader, ModalBody, ModalFooter, TextField, Row, MessageBlock, MessageBlockContent, Col, Dropdown } from 'cfa-react-components';
import { DefaultCaptureConfig, ICaptureConfig } from 'interfaces';
import { FileService, CaptureConfigService, parseAxiosError } from 'services';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import toast from 'react-hot-toast';

interface ICaptureConfigModalProps {
    config: ICaptureConfig,
    editing: boolean,
    onHide: () => void,
}

export function CaptureConfigModal({ config, editing, onHide }: ICaptureConfigModalProps) {
    
    const { control, formState: {isDirty, isValid}, getValues, reset, setValue } = useForm<ICaptureConfig>({
        mode: 'onTouched',
        defaultValues: config,
    });
    const { fields, append, remove } = useFieldArray({
        control, 
        name: "sourceParams.paths",
    });

    const sourceType = useWatch({control, name: "sourceType"});
    const [copyConfig, setCopyConfig] = useState<ICaptureConfig | null>(null);
    const [bucketFieldsDisabled, setBucketfieldsDisabled] = useState(false);

    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const copyExistingConfig = (c: ICaptureConfig) => {
        setCopyConfig(c);
        reset(c);
    }

    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    // use ref to allow button to trigger file input
    // this lets us hide the ugly file input component and use the nice button
    const selectFileRef = useRef(null);
    const selectFile = () => {
        selectFileRef.current.click();
    }

    // reset form fields so user doesnt accidentally submit wrong file url/key
    function handleChange(event) {
        setFile(event.target.files[0]);
        setValue("sourceParams.location", "");
        setValue("sourceParams.paths.0.url", "");
    }

    const uploadFile = () => {

        setUploading(true);

        FileService.upload(file)
            .then((resp) => {
                setBucketfieldsDisabled(true);
                setValue("sourceParams.location", resp.data.bucketUrl, {shouldValidate: true});
                setValue("sourceParams.paths.0.url", resp.data.bucketKey, {shouldValidate: true});
                toast.success("File uploaded");
                setUploading(false);
            })
            .catch(() => {
                toast.error("Error uploading file");
                setUploading(false);
            });
    }
   
    const submitCaptureConfig = () => {

        setLoading(true);
        setErrorMessage(null);

        var payload: ICaptureConfig = getValues();

        // put config if editing
        editing && CaptureConfigService.putCaptureConfig(config.captureConfigUUID, payload)
            .then(() => {
                onHide();
            })
            .catch((e) => {
                setErrorMessage(parseAxiosError(e));
                setLoading(false);
            })

        // post config if creating new
        !editing && CaptureConfigService.postCaptureConfig(payload)
            .then(() => {
                onHide();
            })
            .catch((e) => {
                setErrorMessage(parseAxiosError(e));
                setLoading(false);
            })
    }

    return (<>

        <ModalHeader>
            {editing ? 'Edit Capture Config' : 'New Capture Config'}
        </ModalHeader>

        <ModalBody>
            {errorMessage && (<Row style={{justifyContent: 'center'}}>
                <MessageBlock severity='error'>
                    <MessageBlockContent>{errorMessage.toString()}</MessageBlockContent>
                </MessageBlock>
            </Row>)}
            <div style={{textAlign: "left"}}>
                {!editing && <Row>
                    <Col>
                        <Dropdown
                            label='Copy from existing'
                            fullWidth
                            placeholder='Select (Optional)'
                            value={copyConfig}
                            onChange={(newVal) => copyExistingConfig(newVal)}
                            options={[DefaultCaptureConfig, ...CaptureConfigService.allCaptureConfigs]}
                            renderOption={(c) => <>{c.name != "" ? c.name : "Default"}</>}
                            // must have these options, since we're passing in an array of objects
                            getOptionText={(c) => c.name}
                            getOptionId={(c) => c.captureConfigUUID}
                            disabled={bucketFieldsDisabled}
                        />
                    </Col>
                </Row>}
                <Row>
                    <Col>
                        <Controller name="name" control={control} rules={{required: true}} render={(field) => 
                            <TextField
                                label="Name"
                                spellCheck={false}
                                placeholder="Enter name"
                                required
                                {...field}
                            /> 
                        }/>
                    </Col>
                    <Col>
                        <Controller name="sourceType" control={control} rules={{required: true}} render={(field) => 
                            <Dropdown
                                fullWidth
                                label="Source type"
                                options={["mqtt", "csv"]}
                                disabled={bucketFieldsDisabled || editing}
                                {...field}
                            />
                        }/>
                    </Col>
                </Row>
                <Col>
                    {sourceType == "mqtt" && <Row>
                        <Controller name="sourceParams.secretPath" control={control} rules={{required: true}} render={(field) => 
                            <TextField
                                label="Secret Name"
                                spellCheck={false}
                                placeholder="Enter broker credentials secret name"
                                required
                                {...field}
                            /> 
                        }/>
                    </Row>}
                    {sourceType == "csv" && <Row>
                        <input type="file" ref={selectFileRef} onChange={handleChange} hidden/>
                        <Col style={{paddingLeft: 0}}>
                            <TextField label="Upload File (Optional)" value={file ? file.name : "No file selected"} disabled/>
                        </Col>
                        <Col lg={4} style={{paddingTop: 24, paddingRight: 15}}>
                            <Button onClick={selectFile} variant='outlined'>Select</Button>
                        </Col>
                        <Col lg={4} style={{paddingTop: 24}}>
                            <Button onClick={uploadFile} loading={uploading} disabled={!file}>Upload</Button>
                        </Col>
                    </Row>}
                    <Row>
                        <Col style={{paddingLeft: 0, paddingRight: 0}}>
                            <Controller name="sourceParams.location" control={control} rules={{required: true}} render={(field) => 
                                <TextField
                                    label={sourceType == "mqtt" ? "Broker Url" : "S3 Bucket Url"}
                                    spellCheck={false}
                                    placeholder={sourceType == "mqtt" ? "Enter broker url" : "Enter S3 bucket url"}
                                    disabled={bucketFieldsDisabled}
                                    required
                                    {...field}
                                />
                            }/>
                        </Col>
                    </Row>
                    {fields.map((item, index) => 
                        <Row key={item.id}>
                            <Col style={{paddingLeft: 0}}>
                                {/* make sure to set defaultValue to make controlled input */}
                                <Controller name={`sourceParams.paths.${index}.url`} control={control} defaultValue={item.url} rules={{required: true}} render={(field) => 
                                    <TextField
                                        label={sourceType == "mqtt" ? "Topic" : "S3 Key"}
                                        spellCheck={false}
                                        placeholder={sourceType == "mqtt" ? "Enter mqtt topic" : "Enter S3 key"}
                                        disabled={bucketFieldsDisabled}
                                        required
                                        {...field}
                                    />
                                }/>
                            </Col>
                            <Col lg={4} style={{paddingTop: 24}}>
                                {index == 0 && <Button onClick={() => append({url: ""})} disabled={bucketFieldsDisabled}><FontAwesomeIcon icon="plus"/></Button>}
                                {index != 0 && <Button onClick={() => remove(index)}><FontAwesomeIcon icon="trash" /></Button>}
                            </Col>
                        </Row>
                    )}
                </Col>
                <Row>
                    {sourceType == "mqtt" && (<>
                    <Col>
                        <Controller name="durationMinutes" control={control} rules={{valueAsNumber: true}} render={(field) => 
                            <TextField
                                type="number"
                                label="Duration (minutes)"
                                {...field}
                            /> 
                        }/>
                    </Col>
                    <Col>
                        <Controller name="delayMinutes" control={control} rules={{valueAsNumber: true}} render={(field) => 
                            <TextField
                                type="number"
                                label="Delay (minutes)"
                                {...field}
                            />
                        }/>
                    </Col></>)}
                    <Col>
                        <Controller name="TTL" control={control} rules={{valueAsNumber: true}} render={(field) => 
                            <TextField
                                type="number"
                                label="Message TTL (days)"
                                {...field}
                            /> 
                        }/>
                    </Col>
                    <Col>
                        <Controller name="durationMessages" control={control} rules={{valueAsNumber: true}} render={(field) => 
                            <TextField
                                type="number"
                                label="Message Limit"
                                {...field}
                            /> 
                        }/>
                    </Col>
                </Row>
            </div>
        </ModalBody>

        <ModalFooter>
            <Button variant="outlined" onClick={onHide}>
                Close
            </Button>
            <Button variant="filled" type="submit" loading={loading} onClick={submitCaptureConfig} disabled={!isDirty || !isValid}>
                {!editing && (loading ? 'Submitting...' : 'Submit')}
                {editing && (loading ? 'Saving...' : 'Save')}
            </Button>
        </ModalFooter>

    </>);
}
